import logger from '@/utils/logger';

/**
 * 兼容的请求配置接口
 */
interface RequestConfig extends RequestInit {
  timeout?: number;
  retries?: number;
  retryDelay?: number;
  retryBackoff?: boolean;
}

/**
 * 兼容的响应接口
 */
interface NetworkResponse<T = any> {
  success: boolean;
  data?: T;
  error?: string;
  status?: number;
}

/**
 * 带超时控制的 fetch 请求
 * @param url 请求URL
 * @param options 请求配置
 * @returns Promise<Response>
 */
export const fetchWithTimeout = async (
  url: string,
  options: RequestConfig = {}
): Promise<Response> => {
  const {
    timeout = 10000, // 默认10秒超时
    retries = 0, // 默认不重试
    retryDelay = 1000, // 默认1秒重试延迟
    retryBackoff = true, // 默认使用指数退避
    ...fetchOptions
  } = options;

  const controller = new AbortController();
  const timeoutId = setTimeout(() => controller.abort(), timeout);

  const attemptRequest = async (attempt: number = 0): Promise<Response> => {
    try {
      const response = await fetch(url, {
        ...fetchOptions,
        signal: controller.signal,
      });
      clearTimeout(timeoutId);
      return response;
    } catch (error) {
      clearTimeout(timeoutId);

      // 如果是超时错误且还有重试次数
      if ((error as any).name === 'AbortError' && attempt < retries) {
        const delay = retryBackoff ? retryDelay * 2 ** attempt : retryDelay;
        logger.warn(`[Network] 请求超时，${delay}ms 后进行第 ${attempt + 1} 次重试:`, url);

        await new Promise<void>((resolve) => {
          setTimeout(resolve, delay);
        });
        return attemptRequest(attempt + 1);
      }

      // 其他错误或重试次数用完
      if (!(error instanceof Error)) {
        throw new Error(JSON.stringify(error));
      }
      throw error;
    }
  };

  return attemptRequest();
};

/**
 * 带完整错误处理的网络请求
 * @param url 请求URL
 * @param options 请求配置
 * @returns Promise<NetworkResponse>
 */
export const requestWithErrorHandling = async <T = any>(
  url: string,
  options: RequestConfig = {}
): Promise<NetworkResponse<T>> => {
  try {
    const response = await fetchWithTimeout(url, options);

    if (!response.ok) {
      const errorText = await response.text();
      return {
        success: false,
        error: `HTTP ${response.status}: ${errorText}`,
        status: response.status,
      };
    }

    const data = await response.json();
    return {
      success: true,
      data,
      status: response.status,
    };
  } catch (error) {
    return {
      success: false,
      error: error instanceof Error ? error.message : String(error),
    };
  }
};

/**
 * 场景配置获取专用请求函数
 * @param url 请求URL
 * @param params 请求参数
 * @returns Promise<NetworkResponse>
 */
export const getScenesRequest = async (
  url: string,
  params: Record<string, any> = {}
): Promise<NetworkResponse> => {
  return requestWithErrorHandling(url, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
    },
    body: JSON.stringify(params),
    timeout: 15000, // 场景配置15秒超时
    retries: 2, // 重试2次
    retryDelay: 2000, // 2秒重试延迟
  });
};

/**
 * AIGC控制专用请求函数
 * @param url 请求URL
 * @param params 请求参数
 * @returns Promise<NetworkResponse>
 */
export const aigcControlRequest = async (
  url: string,
  params: Record<string, any> = {}
): Promise<NetworkResponse> => {
  return requestWithErrorHandling(url, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
    },
    body: JSON.stringify(params),
    timeout: 10000, // AIGC 控制 10 秒超时
    retries: 1, // 重试1次
    retryDelay: 1000, // 1秒重试延迟
  });
};

// 通用 sleep
export const sleep = (ms: number): Promise<void> =>
  new Promise<void>((resolve) => {
    setTimeout(() => resolve(), ms);
  });

// 通用异步重试（指数退避可选）
export const retryAsync = async <T>(
  fn: () => Promise<T>,
  options: { retries?: number; delayMs?: number; backoff?: boolean } = {}
): Promise<T> => {
  const { retries = 0, delayMs = 1000, backoff = true } = options;
  const execute = async (attempt: number): Promise<T> => {
    try {
      return await fn();
    } catch (e) {
      if (attempt >= retries) throw e;
      const wait = backoff ? delayMs * 2 ** attempt : delayMs;
      await sleep(wait);
      return execute(attempt + 1);
    }
  };
  return execute(0);
};
